import React, { useState, useEffect, useMemo } from "react";
import Layout from "@/components/Layout";
import { getBetTicker } from "../../services/api";
import api from "../../services/api";
import { API_ENDPOINTS } from "../../config/api";
import { GAME_LIST } from "../../utils/gameList";

export default function BetTicker() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filter1, setFilter1] = useState("All");
  const [filter2, setFilter2] = useState("All");
  const [oddsFilter, setOddsFilter] = useState("All");
  const [stakeFilter, setStakeFilter] = useState("All");
  const [refreshInterval, setRefreshInterval] = useState(1);
  const [loading, setLoading] = useState(false);
  const [betData, setBetData] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      let selectedCategory = undefined;
      let selectedSport = undefined;

      if (filter1 !== 'All') {
        const game = GAME_LIST.find(g => g.value === filter1);
        if (game) {
          selectedCategory = game.category;
          selectedSport = filter1;
        } else {
          selectedSport = filter1;
        }
      }
      // Ticker expects: sport, category
      const params = {
        category: selectedCategory,
        sport: selectedSport
      };

      const response = await getBetTicker(params);
      if (response && response.data) {
        const rawBets = Array.isArray(response.data) ? response.data : (response.data.data || []);

        const mappedBets = rawBets.map(bet => ({
          id: bet.id,
          member: bet.user ? bet.user.username : (bet.username || bet.user_id || '-'),
          eventName: bet.description || bet.event_name || '-',
          market: bet.bet_type || bet.market_name || '-',
          selection: bet.selection_name || bet.selection || '-',
          oddsReq: bet.odds ? parseFloat(bet.odds || 0) : 0,
          aveMatched: bet.odds ? parseFloat(bet.odds || 0) : 0,
          matched: bet.amount ? parseFloat(bet.amount || 0) : (bet.stake_amount ? parseFloat(bet.stake_amount || 0) : 0),
          currency: bet.currency || 'INR',
          profitLiability: bet.liability ? parseFloat(bet.liability).toFixed(2) : "0.00",
          lastUpdate: bet.created_at ? new Date(bet.created_at).toLocaleString() : '-'
        }));

        setBetData(mappedBets);
      }
    } catch (error) {
      console.error("Error fetching bet data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter1, filter2, oddsFilter, stakeFilter]);

  // Debounced User Search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 3) {
        fetchUserSuggestions(searchTerm);
      } else {
        setRecommendations([]);
        setShowDropdown(false);
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchUserSuggestions = async (term) => {
    setSearchLoading(true);
    setShowDropdown(true);
    setRecommendations([]);
    try {
      const response = await api.get(API_ENDPOINTS.SEARCH_BY_USERNAME, {
        params: { username: term }
      });
      if (response.data?.data?.results && Array.isArray(response.data.data.results)) {
        setRecommendations(response.data.data.results);
      } else {
        setRecommendations([]);
      }
    } catch (error) {
      console.error('Error fetching user suggestions:', error);
      setRecommendations([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectUser = (username) => {
    setSearchTerm(username);
    setShowDropdown(false);
    setRecommendations([]);
  };

  const filteredBets = useMemo(() => {
    let filtered = betData;

    if (searchTerm.length >= 3) {
      filtered = filtered.filter((bet) =>
        (bet.member || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (bet.eventName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (bet.selection || "").toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [betData, searchTerm]);

  const handleApply = () => {
    fetchData();
  };

  const handleCancel = () => {
    setFilter1("");
    setFilter2("All");
    setOddsFilter("All");
    setStakeFilter("All");
    setSearchTerm("");
  };

  const handleRefresh = async () => {
    fetchData();
  };

  return (
    <Layout>
      <div className="p-4 w-full mx-auto bg-[#171b36] min-h-screen">
        {/* Header Section */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 pt-4"
        >
          <div className="flex justify-between items-start">
            <div className="w-full mb-1">
              <div
                style={{ color: "#cae0e8", fontSize: "24px" }}
                className="font-semibold mb-1"
              >
                Bet Ticker
              </div>
              <div className="w-full h-px bg-white mb-2"></div>

              {/* Filter Bar */}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <select
                  style={{ width: '20%' }}
                  value={filter1}
                  onChange={(e) => setFilter1(e.target.value)}
                  className="px-2 py-2 text-[12px] bg-gray-200 text-black rounded-sm border-none focus:outline-none"
                >

                  <option value="All">All</option>
                  {GAME_LIST.map((game, index) => (
                    <option key={`${game.value}-${index}`} value={game.value}>
                      {game.label}
                    </option>
                  ))}



                </select>
                <select
                  style={{ width: '20%' }}
                  value={filter2}
                  onChange={(e) => setFilter2(e.target.value)}
                  className="px-2 py-2 text-[12px] bg-gray-200 text-black rounded-sm border-none focus:outline-none"
                >
                  <option value="All">All</option>
                </select>
                <select
                  style={{ width: '10%' }}
                  value={oddsFilter}
                  onChange={(e) => setOddsFilter(e.target.value)}
                  className="px-2 py-2 text-[12px] bg-gray-200 text-black rounded-sm border-none focus:outline-none"
                >
                  <option value="All">Odds: All</option>
                </select>
                <select
                  value={stakeFilter}
                  style={{ width: '10%' }}
                  onChange={(e) => setStakeFilter(e.target.value)}
                  className="px-2 py-2 text-[12px] bg-gray-200 text-black rounded-sm border-none focus:outline-none"
                >
                  <option value="All">Stake: All</option>
                </select>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowDropdown(false);
                    }}
                    placeholder="Enter Atleast 3 character"
                    className="px-2 py-1 w-[180px] text-[11px] bg-gray-200 text-black placeholder-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {showDropdown && (
                    <ul className="absolute top-full left-0 w-[180px] bg-white text-black text-[12px] rounded-sm shadow-lg max-h-40 overflow-y-auto z-50">
                      {searchLoading ? (
                        <li className="px-2 py-2 text-gray-500 italic">Searching...</li>
                      ) : recommendations.length > 0 ? (
                        recommendations.map((user, index) => (
                          <li
                            key={index}
                            onClick={() => handleSelectUser(user.username)}
                            className="px-2 py-2 cursor-pointer hover:bg-blue-100"
                          >
                            {user.username}
                          </li>
                        ))
                      ) : (
                        <li className="px-2 py-2 text-gray-500 italic">No record found</li>
                      )}
                    </ul>
                  )}
                </div>
                <button
                  onClick={handleApply}
                  className="bg-[#11374a] hover:bg-[#1a4f66] text-white text-[12px] font-bold px-3 py-2 transition-colors"
                >
                  Apply
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-[#b71601] hover:bg-red-600 text-white text-[12px] font-bold px-3 py-2 transition-colors"
                >
                  Cancel
                </button>
                <div style={{ fontSize: '12px', color: '#fff', fontWeight: 'bold', marginRight: '10px', marginLeft: '20px' }}>
                  3
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="bg-[#11374a] hover:bg-[#1a4f66] text-white text-[14px] font-medium px-2 py-1  transition-colors"
                >
                  {loading ? "Refreshing..." : "Refresh"}
                </button>
              </div>
              {/* Error Message */}
              <div className="text-red-500 text-[12px] font-medium mb-2">
                Please Select Event
              </div>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 py-2"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Member
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Event Name
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Market
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Selection
                  </th>
                  <th className="text-center px-2 py-1 text-white font-bold text-[14px]">
                    Odds req
                  </th>
                  <th className="text-center text-nowrap px-2 py-1 text-white font-bold text-[14px]">
                    Ave. Matched
                  </th>
                  <th className="text-center px-2 py-1 text-white font-bold text-[14px]">
                    Matched
                  </th>
                  <th className="text-center px-2 py-1 text-white font-bold text-[14px]">
                    Currency
                  </th>
                  <th className="text-center px-2 py-1 text-white font-bold text-[14px]">
                    Liability
                  </th>
                  <th className="text-center px-2 py-1 text-white font-bold text-[14px]">
                    Last Update
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredBets.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-2 py-4 text-center text-gray-400">
                      No bets found
                    </td>
                  </tr>
                ) : (
                  filteredBets.map((bet, index) => (
                    <tr
                      key={bet.id}
                      className={`transition-colors ${index % 2 === 0 ? "bg-[rgba(245,245,245,0.1)]" : ""
                        }`}
                    >
                      <td style={{ width: '10%' }} className="py-1 px-2 text-white font-medium">
                        {bet.member}
                      </td>
                      <td style={{ width: '14%', }} className="py-1 px-2 text-white font-medium">
                        {bet.eventName}
                      </td>
                      <td className="py-1 px-2 text-white font-bold">
                        {bet.market}
                      </td>
                      <td className="py-1 px-2 text-black font-medium" style={{
                        backgroundColor:
                          ['back', 'yes'].includes((bet.market || '').toLowerCase()) ? '#72bbef' :
                            ['lay', 'no'].includes((bet.market || '').toLowerCase()) ? '#faa9ba' :
                              '#ffffff',
                        width: '22%'
                      }}>
                        {bet.selection}
                      </td>
                      <td className="py-1 px-2 text-center text-white font-bold">
                        {bet.oddsReq}
                      </td>
                      <td className="py-1 px-2 text-center text-white font-bold">
                        {bet.aveMatched}
                      </td>
                      <td className="py-1 px-2 text-center text-white font-bold">
                        {bet.matched}
                      </td>
                      <td className="py-1 px-2 text-center text-white font-bold">
                        {bet.currency}
                      </td>
                      <td className="py-1 px-2 text-center text-green-500 font-bold">
                        {bet.profitLiability}
                      </td>
                      <td className="py-1 px-2 text-center text-nowrap text-white text-[13px]">
                        {bet.lastUpdate}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}

